import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import toast from 'react-hot-toast';
import NavHeader from '../components/NavHeader';
import FooterSection from '../components/FooterSection';
import { GlassCard, CountUpNumber } from '../components/shared/GlassCard';
import { Pagination } from '../components/shared/Pagination';
import { GradientText } from '../components/shared/GradientText';
import { getGlobalStats, fetchLastDrawWinnersRows, paginateLastDrawWinnersRows, getWinProbabilityData, getCurrentRoundsOnChain } from '../api/analyticsApi';
import {
  formatAppliedDateRangeLabel,
  formatLocalISODate,
  getDefaultDateRange,
  parseLocalISODate,
} from '../utils/constants.ts';
import GameRoundCard from '../components/analytics/GameRoundCard';

const LAST_DRAW_WINNERS_PAGE_SIZE = 5;
const DEFAULT_ANALYTICS_DATE_RANGE = getDefaultDateRange();
const DATE_PICKER_INPUT_CLASS =
  'w-full min-w-[10rem] rounded-lg border border-white/10 bg-dark/60 px-3 py-2 text-sm text-white outline-none transition-colors focus:border-hash-gold/40';

function AnalyticsDateRangeBar({
  fromSelectedDate,
  toSelectedDate,
  maxSelectableDate,
  appliedRangeLabel,
  loading,
  onDateFromChange,
  onDateToChange,
  onApply,
  onReset,
  description = 'Filters platform stats and draw history. Default: last 24 hours through now.',
}) {
  return (
    <div className="mb-8 rounded-lg border border-white/10 bg-dark/40 p-4">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-gray-500">Date range</p>
          <p className="mt-1 text-xs text-gray-500">{description}</p>
          {appliedRangeLabel && (
            <p className="mt-2 text-xs font-medium text-hash-gold/90">Active: {appliedRangeLabel}</p>
          )}
        </div>
        <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-end hm-analytics-datepicker">
          <label className="flex min-w-[10rem] flex-col gap-1 text-xs text-gray-400">
            From
            <DatePicker
              selected={fromSelectedDate}
              onChange={onDateFromChange}
              maxDate={toSelectedDate || maxSelectableDate}
              dateFormat="yyyy-MM-dd"
              placeholderText="Start date"
              className={DATE_PICKER_INPUT_CLASS}
              calendarClassName="hm-analytics-datepicker-calendar"
              popperClassName="hm-analytics-datepicker-popper"
              showPopperArrow={false}
              isClearable
            />
          </label>
          <label className="flex min-w-[10rem] flex-col gap-1 text-xs text-gray-400">
            To
            <DatePicker
              selected={toSelectedDate}
              onChange={onDateToChange}
              minDate={fromSelectedDate}
              maxDate={maxSelectableDate}
              dateFormat="yyyy-MM-dd"
              placeholderText="End date"
              className={DATE_PICKER_INPUT_CLASS}
              calendarClassName="hm-analytics-datepicker-calendar"
              popperClassName="hm-analytics-datepicker-popper"
              showPopperArrow={false}
              isClearable
            />
          </label>
          <button
            type="button"
            onClick={onApply}
            disabled={loading}
            className="rounded-lg border border-hash-gold/30 bg-gradient-to-r from-hash-gold/20 to-medora-blue/20 px-4 py-2 text-sm font-semibold text-hash-gold transition-colors hover:bg-hash-gold/10 cursor-pointer disabled:cursor-not-allowed disabled:opacity-50"
          >
            Apply
          </button>
          <button
            type="button"
            onClick={onReset}
            disabled={loading}
            className="rounded-lg border border-white/10 px-4 py-2 text-sm font-medium text-gray-300 transition-colors hover:border-white/20 hover:text-white cursor-pointer disabled:cursor-not-allowed disabled:opacity-50"
          >
            Reset
          </button>
        </div>
      </div>
    </div>
  );
}

function Analytics() {
  const [stats, setStats] = useState(null);
  const [probData, setProbData] = useState([]);
  const [gameRounds, setGameRounds] = useState([]);
  const [allLastDrawWinnersRows, setAllLastDrawWinnersRows] = useState(null);
  const [lastDrawWinnersPage, setLastDrawWinnersPage] = useState(1);
  const [analyticsRefreshing, setAnalyticsRefreshing] = useState(true);
  const [dateFrom, setDateFrom] = useState(DEFAULT_ANALYTICS_DATE_RANGE.dateFrom);
  const [dateTo, setDateTo] = useState(DEFAULT_ANALYTICS_DATE_RANGE.dateTo);
  const [appliedDateRange, setAppliedDateRange] = useState(DEFAULT_ANALYTICS_DATE_RANGE);
  const analyticsMountedRef = useRef(true);
  const maxSelectableDate = useMemo(() => new Date(), []);
  const fromSelectedDate = useMemo(() => parseLocalISODate(dateFrom), [dateFrom]);
  const toSelectedDate = useMemo(() => parseLocalISODate(dateTo), [dateTo]);
  const appliedRangeLabel = useMemo(
    () => formatAppliedDateRangeLabel(appliedDateRange),
    [appliedDateRange],
  );

  useEffect(() => {
    analyticsMountedRef.current = true;
    return () => {
      analyticsMountedRef.current = false;
    };
  }, []);

  const loadAnalyticsForDateRange = useCallback(async (dateRange) => {
    setAnalyticsRefreshing(true);
    try {
      const [s, rows] = await Promise.all([
        getGlobalStats({ dateRange, forceRefresh: true }),
        fetchLastDrawWinnersRows(dateRange),
      ]);
      if (!analyticsMountedRef.current) return;
      setStats(s);
      setAllLastDrawWinnersRows(rows);
    } catch (e) {
      console.error('Analytics date-range load failed:', e);
      if (analyticsMountedRef.current) {
        setAllLastDrawWinnersRows([]);
      }
    } finally {
      if (analyticsMountedRef.current) setAnalyticsRefreshing(false);
    }
  }, []);

  useEffect(() => {
    loadAnalyticsForDateRange(appliedDateRange);
  }, [appliedDateRange, loadAnalyticsForDateRange]);

  useEffect(() => {
    let cancelled = false;
    getWinProbabilityData()
      .then((p) => {
        if (!cancelled) setProbData(p);
      })
      .catch((e) => {
        console.error('Analytics probability load failed:', e);
        if (!cancelled) setProbData([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // useEffect(() => {
  //   const onVis = () => {
  //     if (document.visibilityState === 'visible') {
  //       loadAnalyticsForDateRange(appliedDateRange);
  //     }
  //   };
  //   document.addEventListener('visibilitychange', onVis);
  //   return () => document.removeEventListener('visibilitychange', onVis);
  // }, [appliedDateRange, loadAnalyticsForDateRange]); 

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const rows = await getCurrentRoundsOnChain();
        if (!cancelled) setGameRounds(rows);
      } catch (e) {
        console.error('Analytics live rounds load failed:', e);
        if (!cancelled) setGameRounds([]);
      }
    })();
    const t = setInterval(async () => {
      try {
        const rows = await getCurrentRoundsOnChain();
        setGameRounds(rows);
      } catch (e) {
        console.error('Analytics live rounds refresh failed:', e);
      }
    }, 300000);
    return () => {
      cancelled = true;
      clearInterval(t);
    };
  }, []);

  const lastDrawWinnersData = useMemo(() => {
    if (!allLastDrawWinnersRows) {
      return {
        winners: [],
        total: 0,
        totalPages: 1,
        page: 1,
        limit: LAST_DRAW_WINNERS_PAGE_SIZE,
      };
    }
    return paginateLastDrawWinnersRows(allLastDrawWinnersRows, lastDrawWinnersPage, LAST_DRAW_WINNERS_PAGE_SIZE);
  }, [allLastDrawWinnersRows, lastDrawWinnersPage]);

  const handleApplyDateRange = useCallback(() => {
    if (dateFrom && dateTo && dateFrom > dateTo) {
      toast.error('Start date must be on or before end date');
      return;
    }
    const defaults = getDefaultDateRange();
    setAppliedDateRange({
      dateFrom: dateFrom || defaults.dateFrom,
      dateTo: dateTo || defaults.dateTo,
    });
    setLastDrawWinnersPage(1);
  }, [dateFrom, dateTo]);

  const handleClearDateRange = useCallback(() => {
    const defaults = getDefaultDateRange();
    setDateFrom(defaults.dateFrom);
    setDateTo(defaults.dateTo);
    setAppliedDateRange(defaults);
    setLastDrawWinnersPage(1);
  }, []);

  const showInitialLoader = !stats && analyticsRefreshing;
  const showRefreshBar = analyticsRefreshing;

  const handleLastDrawWinnersPageChange = useCallback((page) => {
    setLastDrawWinnersPage(page);
  }, []);

  const copyUserAddress = useCallback(async (addr) => {
    if (!addr) return;
    try {
      await navigator.clipboard.writeText(addr);
      toast.success('Address copied');
    } catch {
      toast.error('Could not copy address');
    }
  }, []);

  const statCards = useMemo(() => {
    if (!stats) return [];
    return [
      { label: 'Total Players', value: stats.totalPlayers, suffix: '' },
      { label: 'Tickets Sold', value: stats.totalTicketsSold, suffix: '' },
      { label: 'Jackpot Pool', value: stats.currentJackpotPool / 10, prefix: '$', suffix: '', decimals: 2 },
    ];
  }, [stats]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-dark via-dark-secondary to-dark relative">
      {showRefreshBar && (
        <div
          className="fixed left-0 right-0 top-20 z-[60] flex flex-col border-b border-hash-gold/10 shadow-[0_4px_24px_rgba(0,0,0,0.35)]"
          aria-busy="true"
        >
          <div
            className="h-1 w-full overflow-hidden bg-hash-gold/15 shadow-[0_1px_0_rgba(0,0,0,0.4)]"
            role="progressbar"
            aria-valuetext="Loading analytics"
          >
            <div className="hm-analytics-bar-fill" />
          </div>
          {showInitialLoader && (
            <div
              className="flex items-center justify-center gap-2 border-b border-hash-gold/15 bg-dark/95 py-2 px-3 backdrop-blur-md sm:py-2.5"
              role="status"
              aria-live="polite"
            >
              <span
                className="h-1.5 w-1.5 shrink-0 rounded-full bg-hash-gold shadow-[0_0_8px_rgba(238,212,91,0.6)] motion-safe:animate-pulse"
                aria-hidden
              />
              <span className="text-center text-[11px] font-medium tracking-wide text-hash-gold/95 sm:text-xs">
                Loading platform stats…
              </span>
            </div>
          )}
        </div>
      )}
      <div
        className="fixed inset-0 bg-cover bg-center bg-no-repeat pointer-events-none"
        style={{
          backgroundImage: 'url(/images/background-analytics.jpg)',
          opacity: 0.15,
        }}
      />
      <NavHeader />

      <main className={`pb-16 px-4 sm:px-6 lg:px-8 ${showInitialLoader ? 'pt-nav-loading' : 'pt-nav'}`}>
        <div className="container-premium max-w-6xl mx-auto">
          <h1 className="page-title text-3xl sm:text-4xl lg:text-5xl font-bold text-center">
            <GradientText variant="hash" className="text-3xl sm:text-4xl lg:text-5xl font-bold">Platform</GradientText>{' '}
            <GradientText variant="medora" className="text-3xl sm:text-4xl lg:text-5xl font-bold">Analytics</GradientText>
          </h1>
          <p className="page-subtitle text-gray-400 text-center">Real-time insights into HashMedora</p>

          <AnalyticsDateRangeBar
            fromSelectedDate={fromSelectedDate}
            toSelectedDate={toSelectedDate}
            maxSelectableDate={maxSelectableDate}
            appliedRangeLabel={appliedRangeLabel}
            loading={analyticsRefreshing}
            onDateFromChange={(date) => setDateFrom(date ? formatLocalISODate(date) : '')}
            onDateToChange={(date) => setDateTo(date ? formatLocalISODate(date) : '')}
            onApply={handleApplyDateRange}
            onReset={handleClearDateRange}
          />

          {/* Stats Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-10">
            {statCards.map((s, i) => (
              <GlassCard key={i} variant="gold" className="p-6 text-center">
                <div className="text-2xl sm:text-3xl font-bold text-hash-gold text-shadow-gold mb-2">
                  <CountUpNumber
                    end={s.value}
                    prefix={s.prefix || ''}
                    suffix={s.suffix}
                    decimals={s.decimals ?? 0}
                  />
                </div>
                <div className="text-gray-500 text-sm">{s.label}</div>
              </GlassCard>
            ))}
          </div>

          {/* Live rounds (on-chain) */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3 mb-10">
            {gameRounds.map((row) => (
              <GameRoundCard key={row.gameId} row={row} />
            ))}
          </div>

          <div className="flex flex-col gap-6">
            {/* Last Draw winner and number */}
            <GlassCard variant="gold" fill className="min-h-[420px] p-0">
              <div className="flex flex-1 flex-col p-6 pb-0 min-h-[360px]">
                <h2 className="mb-4 shrink-0 text-xl font-bold text-white">Last Draw winner and number</h2>
                {analyticsRefreshing && (
                  <div className="mb-3 shrink-0" aria-busy="true" role="status" aria-live="polite">
                    <div
                      className="h-1 w-full overflow-hidden rounded-full bg-hash-gold/15"
                      role="progressbar"
                      aria-valuetext="Loading draw results"
                    >
                      <div className="hm-analytics-bar-fill" />
                    </div>
                    <p className="mt-2 text-center text-xs font-medium text-hash-gold/90">
                      Loading draw results…
                    </p>
                  </div>
                )}
                <div className={`min-h-0 flex-1 overflow-x-auto ${analyticsRefreshing ? 'opacity-50 pointer-events-none' : ''}`}>
                <table className="dark-table dark-table-no-row-hover w-full">
                  <thead>
                    <tr>
                      <th>Game number</th>
                      <th>Winning amount</th>
                      <th>Winning number</th>
                      <th>Wallet address</th>
                      <th>Transaction</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lastDrawWinnersData.winners.map((w) => (
                      <tr key={w.key}>
                        <td className="text-white font-medium">{w.gameRoundLabel}</td>
                        <td className="text-hash-gold">
                          {typeof w.prize === 'number'
                            ? w.prize.toLocaleString(undefined, { maximumFractionDigits: 6 })
                            : '—'}{' '}
                          USDT
                        </td>
                        <td className="font-mono text-sm font-semibold tabular-nums text-hash-gold/95">
                          {w.winningNumberDisplay}
                        </td>
                        <td
                          className={
                            w.walletAddress
                              ? 'font-mono text-sm text-gray-200 cursor-pointer select-none rounded px-1 py-0.5 -mx-1 transition-colors hover:bg-hash-gold/10 hover:text-hash-gold focus:outline-none focus-visible:ring-2 focus-visible:ring-hash-gold/50'
                              : 'font-mono text-sm text-gray-200'
                          }
                          title={w.walletAddress ? `${w.walletAddress} — click to copy` : undefined}
                          onClick={() => w.walletAddress && copyUserAddress(w.walletAddress)}
                          onKeyDown={(e) => {
                            if (!w.walletAddress) return;
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              copyUserAddress(w.walletAddress);
                            }
                          }}
                          role={w.walletAddress ? 'button' : undefined}
                          tabIndex={w.walletAddress ? 0 : undefined}
                        >
                          {w.userDisplay}
                        </td>
                        <td className="text-sm">
                          {w.txHash ? (
                            <a
                              href={`https://bscscan.com/tx/${w.txHash}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-medora-blue hover:text-hash-gold font-mono underline-offset-2 hover:underline"
                            >
                              {`${w.txHash.slice(0, 8)}…${w.txHash.slice(-6)}`}
                            </a>
                          ) : (
                            <span className="text-gray-500">—</span>
                          )}
                        </td>
                      </tr>
                    ))}
                    {lastDrawWinnersData.winners.length === 0 && !analyticsRefreshing && (
                      <tr>
                        <td colSpan={5} className="text-center text-gray-500 py-4">
                          No draw results for this date range.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
                </div>
              </div>
              <div className="mt-auto shrink-0 border-t border-white/10 px-6 py-3 w-[90%] mx-auto mb-3">
                <Pagination
                  alwaysShow
                  className="!mt-0 !border-t-0 !pt-0"
                  page={lastDrawWinnersData.page}
                  totalPages={lastDrawWinnersData.totalPages}
                  total={lastDrawWinnersData.total}
                  pageSize={lastDrawWinnersData.limit ?? LAST_DRAW_WINNERS_PAGE_SIZE}
                  onPageChange={handleLastDrawWinnersPageChange}
                />
              </div>
            </GlassCard>
          </div>

          {/* Probability Chart */}
          <div className="my-10">
            <h2 className="section-title text-xl font-bold text-white text-center">Win Probability by Game</h2>
            {/* Mobile: single column list */}
            <div className="lg:hidden space-y-4">
              {probData.map((p) => (
                <GlassCard key={p.name} variant="gold" className="p-4">
                  <div className="flex items-center gap-3 mb-2">
                    <img src={p.icon} alt="" className="w-8 h-8 object-contain flex-shrink-0" />
                    <div className="flex-1 flex justify-between text-sm">
                      <span className="text-white font-semibold">{p.name}</span>
                      <span className="text-hash-gold">{p.probability.toFixed(2)}%</span>
                    </div>
                  </div>
                  <div className="h-3 bg-dark-surface rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all duration-1000"
                      style={{
                        width: `${Math.min(p.probability * 3, 100)}%`,
                        background: 'linear-gradient(90deg, #eed45b 0%, #11acec 60%, transparent 100%)',
                      }}
                    />
                  </div>
                  <div className="grid grid-cols-3 text-xs text-gray-500 mt-1">
                    <span>Direct: {p.directProbability.toFixed(3)}%</span>
                    <span className="text-center">Neighbor: {p.neighborProbability.toFixed(1)}%</span>
                    <span className="text-right">Prize: {p.maxPrize.toLocaleString()} USD</span>
                  </div>
                </GlassCard>
              ))}
            </div>
            {/* Desktop: 2-col grid for first 4, last centered */}
            <div className="hidden lg:grid lg:grid-cols-2 gap-4">
              {probData.slice(0, 4).map((p) => (
                <GlassCard key={p.name} variant="gold" className="p-5">
                  <div className="flex items-center gap-3 mb-2">
                    <img src={p.icon} alt="" className="w-10 h-10 object-contain flex-shrink-0" />
                    <div className="flex-1 flex justify-between text-sm">
                      <span className="text-white font-semibold">{p.name}</span>
                      <span className="text-hash-gold">{p.probability.toFixed(2)}%</span>
                    </div>
                  </div>
                  <div className="h-3 bg-dark-surface rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all duration-1000"
                      style={{
                        width: `${Math.min(p.probability * 3, 100)}%`,
                        background: 'linear-gradient(90deg, #eed45b 0%, #11acec 60%, transparent 100%)',
                      }}
                    />
                  </div>
                  <div className="grid grid-cols-3 text-xs text-gray-500 mt-2">
                    <span>Direct: {p.directProbability.toFixed(3)}%</span>
                    <span className="text-center">Neighbor: {p.neighborProbability.toFixed(1)}%</span>
                    <span className="text-right">Prize: {p.maxPrize.toLocaleString()} USD</span>
                  </div>
                </GlassCard>
              ))}
            </div>
            {probData.length > 4 && (
              <div className="hidden lg:flex justify-center mt-4">
                <GlassCard variant="gold" className="p-5 w-full max-w-xl">
                  {(() => {
                    const p = probData[4];
                    return (
                      <>
                        <div className="flex items-center gap-3 mb-2">
                          <img src={p.icon} alt="" className="w-10 h-10 object-contain flex-shrink-0" />
                          <div className="flex-1 flex justify-between text-sm">
                            <span className="text-white font-semibold">{p.name}</span>
                            <span className="text-hash-gold">{p.probability.toFixed(2)}%</span>
                          </div>
                        </div>
                        <div className="h-3 bg-dark-surface rounded-full overflow-hidden">
                          <div
                            className="h-full rounded-full transition-all duration-1000"
                            style={{
                              width: `${Math.min(p.probability * 3, 100)}%`,
                              background: 'linear-gradient(90deg, #eed45b 0%, #11acec 60%, transparent 100%)',
                            }}
                          />
                        </div>
                        <div className="grid grid-cols-3 text-xs text-gray-500 mt-2">
                          <span>Direct: {p.directProbability.toFixed(3)}%</span>
                          <span className="text-center">Neighbor: {p.neighborProbability.toFixed(1)}%</span>
                          <span className="text-right">Prize: {p.maxPrize.toLocaleString()} USD</span>
                        </div>
                      </>
                    );
                  })()}
                </GlassCard>
              </div>
            )}
          </div>
        </div>
      </main>

      <FooterSection />
    </div>
  );
}

export default Analytics;
